import React, { useState, useEffect } from 'react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import OTPLoginModal from './OTPLoginModal';
import { toast } from '../hooks/use-toast';
import {
  X, Loader2, CheckCircle2, Calendar, MapPin, Clock, GraduationCap, ShieldCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = process.env.REACT_APP_COMMUNITY_GAMES_RAZORPAY_KEY_ID || process.env.REACT_APP_RAZORPAY_KEY_ID;

const normPhone = (s) => (s || '').replace(/\D/g, '').slice(0, 10);
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const fmtDays = (days = []) => days.map(d => DAY_SHORT[d] || d).join(' & ');
const fmtTime12 = (t) => {
  if (!t || !t.includes(':')) return t || '';
  const [h, m] = t.split(':');
  const hi = parseInt(h, 10);
  const suf = hi < 12 ? 'AM' : 'PM';
  const h12 = (hi % 12) || 12;
  return `${h12}:${m} ${suf}`;
};

/**
 * CoachingRegisterModal — preserves form state through OTP login.
 * Props:
 *   programme — public programme detail
 *   pkg       — selected package
 *   onClose() — caller closes the modal
 *   onRegistered(reg) — caller reloads programme after success
 */
const CoachingRegisterModal = ({ programme, pkg, onClose, onRegistered }) => {
  const { user, isAuthenticated } = useCustomerAuth();
  const [phase, setPhase] = useState('form'); // form | otp | submitting | success
  const [submitting, setSubmitting] = useState(false);
  const [registration, setRegistration] = useState(null);

  // Form state — preserved across OTP login (component never unmounts)
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(normPhone(user?.phone));
  const [email, setEmail] = useState(user?.email || '');

  const isFree = (pkg.price || 0) === 0;
  const amount = pkg.price || 0;

  // Hydrate from logged-in user (post-OTP)
  useEffect(() => {
    if (user) {
      setName(prev => prev || user.name || '');
      setPhone(prev => prev || normPhone(user.phone));
      setEmail(prev => prev || user.email || '');
    }
  }, [user]);

  const formValid = name.trim().length > 0 && normPhone(phone).length === 10;

  const handleStart = () => {
    if (!formValid) return;
    if (!isAuthenticated) {
      setPhase('otp');
      return;
    }
    submit();
  };

  const handleLoginSuccess = () => {
    setPhase('form');
    // Form state intact — auto-submit after a beat so context updates
    setTimeout(() => submit(), 200);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        name: name.trim(),
        phone: normPhone(phone),
        email: email.trim() || undefined,
      };
      const res = await fetch(
        `${API_URL}/api/coaching/public/${programme.id}/packages/${pkg.id}/register`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Registration failed');
      }
      const data = await res.json();

      if (data.is_free) {
        setRegistration({ ...data.registration, amount_paid: 0 });
        setPhase('success');
        return;
      }

      // Paid path — open Razorpay
      const options = {
        key: data.key_id || RAZORPAY_KEY,
        amount: data.amount,
        currency: data.currency,
        name: programme.venue_name,
        description: `${programme.title} — ${pkg.label}`,
        order_id: data.order_id,
        prefill: {
          contact: `+91${normPhone(phone)}`,
          name,
          email: email || undefined,
        },
        theme: { color: '#0A2C93' },
        handler: async (response) => {
          try {
            const v = await fetch(
              `${API_URL}/api/coaching/public/${programme.id}/packages/${pkg.id}/verify-payment`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              },
            );
            if (!v.ok) {
              const err = await v.json().catch(() => ({}));
              throw new Error(err.detail || 'Payment verification failed');
            }
            const final = await v.json();
            setRegistration({
              id: final.registration_id,
              amount_paid: amount,
              player_name: name,
              player_phone: normPhone(phone),
              player_email: email,
            });
            setPhase('success');
          } catch (err) {
            toast({ title: 'Payment failed', description: err.message, variant: 'destructive' });
            setPhase('form');
          }
        },
        modal: {
          ondismiss: () => {
            toast({ title: 'Payment cancelled', description: 'You can try again anytime.', variant: 'destructive' });
            setPhase('form');
            setSubmitting(false);
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // ============== Success screen ==============
  if (phase === 'success' && registration) {
    return (
      <ModalShell onClose={onClose} testId="coaching-register-success">
        <div className="px-5 pt-5 pb-6 text-center max-h-[90vh] overflow-y-auto">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900" data-testid="success-title">You're registered!</h2>
          <p className="text-sm text-gray-500 mt-1">{programme.title}</p>

          <div className="bg-gray-50 rounded-xl p-4 mt-4 text-left space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <GraduationCap className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span><strong>{pkg.label}</strong> · {pkg.sessions_total} sessions</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span>{fmtDays(programme.days_of_week)} · {fmtTime12(programme.start_time)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span>{programme.venue_name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm pt-2 border-t">
              <span className="text-gray-500">Amount paid</span>
              <span className="ml-auto font-bold" data-testid="success-amount">
                {registration.amount_paid > 0 ? `₹${registration.amount_paid}` : 'Free'}
              </span>
            </div>
          </div>

          <p className="text-sm text-gray-600 mt-4 leading-relaxed" data-testid="success-message">
            We will contact you with further details before your first session.
          </p>

          <button
            onClick={() => { onRegistered(registration); onClose(); }}
            className="w-full mt-5 py-3 rounded-xl bg-[#0A2C93] text-white text-sm font-semibold hover:bg-[#082273]"
            data-testid="success-done"
          >
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <>
      <ModalShell onClose={onClose} testId="coaching-register-modal">
        <div className="px-5 pt-4 pb-5 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 leading-tight">Register</h2>
              <p className="text-xs text-gray-500 mt-0.5 truncate">{programme.title}</p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="modal-close">
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Package summary */}
          <div className="bg-[#0A2C93]/5 border border-[#0A2C93]/10 rounded-xl p-3 mb-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{pkg.label}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {pkg.sessions_total} sessions · {fmtDays(programme.days_of_week)} · {fmtTime12(programme.start_time)}
                </p>
              </div>
              <p className="text-lg font-bold text-[#0A2C93] flex-shrink-0">
                {isFree ? 'Free' : `₹${amount}`}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-3">
            <FieldLabel label="Your name" required>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Full name"
                className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                data-testid="reg-name"
              />
            </FieldLabel>
            <FieldLabel label="Phone number" required>
              <div className="flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#0A2C93]/30">
                <span className="bg-gray-50 px-2.5 py-2.5 text-sm text-gray-500 border-r">+91</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(normPhone(e.target.value))}
                  placeholder="10-digit mobile"
                  inputMode="numeric"
                  className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                  data-testid="reg-phone"
                />
              </div>
            </FieldLabel>
            <FieldLabel label="Email (optional)" helper="We'll send you a confirmation with all the details">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                data-testid="reg-email"
              />
            </FieldLabel>
          </div>

          {/* Secure payment note */}
          {!isFree && (
            <div className="mt-4 flex items-center gap-2 text-[11px] text-gray-500">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              <span>Secure payment via Razorpay. Full refund on cancellation.</span>
            </div>
          )}

          {/* CTA */}
          <button
            onClick={handleStart}
            disabled={!formValid || submitting}
            className="w-full mt-5 py-3 rounded-xl bg-[#0A2C93] text-white text-sm font-semibold hover:bg-[#082273] disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="reg-submit"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
            ) : isFree ? (
              'Confirm registration'
            ) : (
              <>Pay ₹{amount} <Clock className="h-3.5 w-3.5 opacity-60" /></>
            )}
          </button>
        </div>
      </ModalShell>

      {phase === 'otp' && (
        <OTPLoginModal
          onClose={() => setPhase('form')}
          onSuccess={handleLoginSuccess}
          contextLabel="to complete your registration"
        />
      )}
    </>
  );
};

// ============== Shared ==============

const ModalShell = ({ children, onClose, testId }) => (
  <div
    className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center sm:p-4"
    onClick={onClose}
    data-testid={testId}
  >
    <div
      className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl"
      onClick={e => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

const FieldLabel = ({ label, required, helper, children }) => (
  <label className="block">
    <div className="text-xs font-semibold text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </div>
    {children}
    {helper && <p className="text-[11px] text-gray-500 mt-1">{helper}</p>}
  </label>
);

export default CoachingRegisterModal;
